import React, { useEffect, useRef, useState } from 'react';
import PaginationFooter from './PaginationFooter.jsx';

/**
 * Componente GenericTables - Tabla reutilizable con configuracion de columnas
 *
 * @param {Object} props
 * @param {Array} props.columns - Array de configuracion de columnas
 * @param {Array} props.data - Array de datos a mostrar
 * @param {string} props.emptyMessage - Mensaje cuando no hay datos
 * @param {number} props.emptyColSpan - Colspan para mensaje vacio
 * @param {boolean} props.zebraStripe - Activar rayas zebra (alternating colors)
 * @param {boolean} props.showFooter - Mostrar footer con paginacion
 * @param {Object} props.pagination - Configuracion de paginacion
 * @param {number} props.pagination.currentPage - Pagina actual
 * @param {number} props.pagination.totalPages - Total de paginas
 * @param {number} props.pagination.pageSize - Tamano de pagina actual
 * @param {number} props.pagination.totalItems - Total de items
 * @param {Array} props.pagination.pageSizeOptions - Opciones de tamano de pagina
 * @param {Function} props.pagination.onPageChange - Callback cuando cambia pagina
 * @param {Function} props.pagination.onPageSizeChange - Callback cuando cambia tamano de pagina
 * @param {"cards"|"legacy"} props.mobileCardVariant - Presentación de las filas en mobile.
 * @param {boolean} props.columns[].isChip - Muestra la columna como chip en la esquina superior izquierda de la tarjeta.
 * @param {boolean} props.columns[].isAction - Muestra las acciones en la esquina superior derecha de la tarjeta.
 * @param {boolean} props.columns[].mobilePrimary - Usa la columna como título de la tarjeta.
 * @param {boolean} props.columns[].mobileHidden - Oculta una columna solo en la tarjeta mobile.
 * @param {"inline"|"top-left"} props.columns[].mobileCardPosition - Ajusta la posición en mobile.
 */
const GenericTables = ({
  columns = [],
  data = [],
  emptyMessage = 'No hay datos disponibles',
  emptyColSpan,
  zebraStripe = true,
  showFooter = false,
  pagination = null,
  onRowClick = null,
  getRowClassName = null,
  loading = false,
  loadingMessage = 'Cargando...',
  tableContainerClassName = '',
  mobileCardVariant = 'cards'
}) => {
  const colSpan = emptyColSpan || columns.length;
  const desktopHeaderRef = useRef(null);
  const [desktopHeaderHeight, setDesktopHeaderHeight] = useState(0);
  const showEmptyState = !loading && data.length === 0;
  const showLoadingPlaceholder = loading && data.length === 0;
  const containerClassName = [
    showLoadingPlaceholder ? 'min-h-[220px]' : '',
    tableContainerClassName
  ]
    .filter(Boolean)
    .join(' ');

  useEffect(() => {
    const updateDesktopHeaderHeight = () => {
      setDesktopHeaderHeight(desktopHeaderRef.current?.getBoundingClientRect().height || 0);
    };

    updateDesktopHeaderHeight();

    if (typeof ResizeObserver !== 'undefined' && desktopHeaderRef.current) {
      const observer = new ResizeObserver(() => updateDesktopHeaderHeight());
      observer.observe(desktopHeaderRef.current);
      return () => observer.disconnect();
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateDesktopHeaderHeight);
      return () => window.removeEventListener('resize', updateDesktopHeaderHeight);
    }

    return undefined;
  }, [columns.length]);

  const renderCellContent = (column, row, index) =>
    column.render ? column.render(row, index) : row[column.key];

  const renderMobileCard = (row, index) => {
    const statusColumn = columns.find((column) => column.key === 'status');
    const chipColumns = columns.filter((column) => column.isChip || column.key === 'status');
    const topLeftColumns = columns.filter(
      (column) => column.mobileCardPosition === 'top-left' && !chipColumns.includes(column)
    );
    const topActionColumns = columns.filter(
      (column) => column.isAction && column.mobileCardPosition !== 'inline'
    );
    const inlineActionColumns = columns.filter(
      (column) => column.isAction && column.mobileCardPosition === 'inline'
    );
    const contentColumns = columns.filter(
      (column) => !column.isAction && !column.mobileHidden && !chipColumns.includes(column) && !topLeftColumns.includes(column)
    );
    const primaryColumn = contentColumns.find((column) => column.mobilePrimary) || contentColumns[0];
    const detailColumns = contentColumns.filter((column) => column !== primaryColumn);

    if (mobileCardVariant === 'legacy') {
      const actionColumn = topActionColumns[0];

      return (
        <div
          key={index}
          className={`rounded-xl shadow-md p-4 space-y-4 bg-surface border border-line ${onRowClick ? 'cursor-pointer select-none transition-colors hover:bg-surface-2' : ''} ${getRowClassName ? getRowClassName(row, index) : ''}`}
          onClick={onRowClick ? () => onRowClick(row, index) : undefined}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0 text-content text-base font-semibold leading-tight">
              {renderCellContent(columns[0], row, index)}
            </div>
            {statusColumn && (
              <div className="flex-shrink-0">
                {renderCellContent(statusColumn, row, index)}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm text-content">
            {columns
              .filter((column) => !column.isAction && column.key !== 'status' && column === columns[1])
              .map((column) => (
                <div key={column.key} className="flex flex-col gap-1">
                  <span className="text-[11px] uppercase tracking-wide text-content-muted font-semibold">
                    {column.label}
                  </span>
                  <div className="font-semibold text-content leading-snug break-words">
                    {renderCellContent(column, row, index)}
                  </div>
                </div>
              ))}
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm text-content">
            {columns
              .filter((column) => !column.isAction && column.key !== 'status' && column !== columns[0] && column !== columns[1])
              .map((column) => (
                <div key={column.key} className="flex flex-col gap-1">
                  <span className="text-[11px] uppercase tracking-wide text-content-muted font-semibold">
                    {column.label}
                  </span>
                  <div className="font-semibold text-content leading-snug break-words">
                    {renderCellContent(column, row, index)}
                  </div>
                </div>
              ))}
          </div>

          {actionColumn && (
            <div className="pt-2 mt-2" onClick={(event) => event.stopPropagation()}>
              <div className="w-full flex flex-col gap-2">
                {renderCellContent(actionColumn, row, index)}
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div
        key={index}
        className={`rounded-xl border border-line bg-surface p-4 shadow-md space-y-4 ${onRowClick ? 'cursor-pointer select-none transition-colors hover:bg-surface-2' : ''} ${getRowClassName ? getRowClassName(row, index) : ''}`}
        onClick={onRowClick ? () => onRowClick(row, index) : undefined}
      >
        {(chipColumns.length > 0 || topLeftColumns.length > 0 || topActionColumns.length > 0 || primaryColumn) && (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              {(chipColumns.length > 0 || topLeftColumns.length > 0) ? (
                <div className="flex flex-wrap items-center gap-2">
                  {[...chipColumns, ...topLeftColumns].map((column) => (
                    <div key={column.key}>{renderCellContent(column, row, index)}</div>
                  ))}
                </div>
              ) : primaryColumn ? (
                <div className="text-base font-semibold leading-tight text-content break-words">
                  {renderCellContent(primaryColumn, row, index)}
                </div>
              ) : null}
            </div>

            {topActionColumns.length > 0 && (
              <div
                className="flex flex-shrink-0 flex-wrap items-center justify-end gap-2"
                onClick={(event) => event.stopPropagation()}
              >
                {topActionColumns.map((column) => (
                  <div key={column.key}>{renderCellContent(column, row, index)}</div>
                ))}
              </div>
            )}
          </div>
        )}

        {(chipColumns.length > 0 || topLeftColumns.length > 0) && primaryColumn && (
          <div className="text-base font-semibold leading-tight text-content break-words">
            {renderCellContent(primaryColumn, row, index)}
          </div>
        )}

        {detailColumns.length > 0 && (
          <div className="grid grid-cols-1 gap-x-4 gap-y-3 text-sm min-[420px]:grid-cols-2">
            {detailColumns.map((column) => (
              <div key={column.key} className="min-w-0 space-y-1">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-content-muted">
                  {column.label}
                </span>
                <div className="break-words font-medium leading-snug text-content">
                  {renderCellContent(column, row, index)}
                </div>
              </div>
            ))}
          </div>
        )}

        {inlineActionColumns.length > 0 && (
          <div className="flex items-center gap-2 border-t border-line pt-3" onClick={(event) => event.stopPropagation()}>
            {inlineActionColumns.map((column) => (
              <div key={column.key}>{renderCellContent(column, row, index)}</div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderLoadingOverlay = (className, style) => (
    <div
      className={`pointer-events-none absolute z-10 flex items-center justify-center bg-bg/60 backdrop-blur-[1px] px-4 ${className}`.trim()}
      style={style}
    >
      <div className="flex items-center gap-3 rounded-lg border border-line bg-surface/90 px-4 py-3 text-sm text-content shadow-lg">
        <span className="h-5 w-5 animate-spin rounded-full border-2 border-amber border-t-transparent" />
        {loadingMessage}
      </div>
    </div>
  );

  return (
    <div className="rounded-xl md:bg-surface md:shadow-lg md:border md:border-line md:overflow-hidden flex flex-col">
      <div className={`relative block md:hidden flex-1 ${containerClassName}`.trim()}>
        <div className={`space-y-3 ${loading ? 'blur-[1px]' : ''}`.trim()}>
          {showEmptyState ? (
            <div className="text-center py-6 text-content-muted text-sm">{emptyMessage}</div>
          ) : showLoadingPlaceholder ? (
            <div className="h-full min-h-[220px]" aria-hidden="true" />
          ) : (
            data.map(renderMobileCard)
          )}
        </div>

        {loading && renderLoadingOverlay('inset-0 rounded-xl')}
      </div>

      <div className={`relative hidden md:block overflow-x-auto ${containerClassName}`.trim()}>
        <table className="w-full">
          <thead ref={desktopHeaderRef} className="bg-table-head shadow-sm">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-6 py-4 text-${column.align || 'center'} text-sm font-bold text-content border-b-2 border-line`}
                  style={column.width ? { width: column.width, minWidth: column.width, maxWidth: column.width } : {}}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className={`bg-surface divide-y divide-line ${loading ? 'blur-[1px]' : ''}`.trim()}>
            {showEmptyState ? (
              <tr>
                <td colSpan={colSpan} className="text-center py-8 text-content-muted">
                  {emptyMessage}
                </td>
              </tr>
            ) : showLoadingPlaceholder ? (
              <tr aria-hidden="true">
                <td colSpan={colSpan} className="py-8">
                  <div className="min-h-[140px]" />
                </td>
              </tr>
            ) : (
              data.map((row, index) => (
                <tr
                  key={index}
                  className={`${zebraStripe
                    ? 'odd:!bg-surface even:!bg-surface-2'
                    : 'bg-surface'
                  } ${onRowClick ? 'cursor-pointer select-none hover:!bg-[var(--app-row-hover)] transition-colors' : ''} ${getRowClassName ? getRowClassName(row, index) : ''}`}
                  onClick={onRowClick ? () => onRowClick(row, index) : undefined}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={`px-6 py-4 align-middle ${column.isAction ? '' : 'whitespace-nowrap'}`}
                      data-label={column.label}
                      style={column.width ? { width: column.width, minWidth: column.width, maxWidth: column.width } : {}}
                    >
                      {column.isAction ? (
                        <div
                          className={`flex items-center justify-${column.align || 'center'} gap-2`}
                          onClick={(event) => event.stopPropagation()}
                        >
                          {renderCellContent(column, row, index)}
                        </div>
                      ) : (
                        <div className={`flex items-center justify-${column.align || 'center'} text-sm text-content`}>
                          {renderCellContent(column, row, index)}
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>

        {loading && renderLoadingOverlay('inset-x-0 bottom-0', { top: `${desktopHeaderHeight}px` })}
      </div>

      <PaginationFooter pagination={pagination} loading={loading} showFooter={showFooter} />
    </div>
  );
};

export default GenericTables;
